import * as yup from "yup"

import { Flex, Form, Row } from "antd"
import {
  Button,
  Icon,
  Legend,
  Modal,
  PhoneInput,
  SelectInput,
  Spinner,
  Text,
  notify,
  notification,
  Group,
  RadioButtons,
  TextInput,
} from "components/common"
import {
  useAddLeadMutation,
  useChangeAssigneeMutation,
  useGetReasonForAllocationQuery,
  useLazyGetClientDetailQuery,
} from "features/clients/detail/api"
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useForm, useWatch } from "antd/lib/form/Form"
import { IconText } from "components/custom"
import { regex } from "constants/regex"
import { AddLeadForm } from "features/leads/components"
import { useLazyGetCheckEmailQuery, useLazyGetCheckPhoneNumberQuery } from "features/shared/api"
import { schemaRules } from "features/shared/utils"
import { useQueryUpdate } from "hooks"
import _ from "lodash"
import { useSelector } from "react-redux"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import theme from "utils/themeVars"
import { AddClientForm } from ".."
import { useTranslation } from "react-i18next"
import AffiliateSection from "../clientForm/affiliateSection"
import { affiliateOptions } from "constants/list"
import getClientLeadValidationSchema from "./clientLeadValidationSchema"
import parentApi from "store/parentApi"
import getPrivilege from "utils/privileges/privileges"
import ClientProfileForm from "features/tasks/components/clientProfileForm/clientProfileForm"
import { isValidEmail } from "utils"
import { getEmailRules, getPhoneRules } from "features/clients/clientFormsHelperFucntions"

const ClientLeadForm = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const [initializeClientForm] = useForm()
  const addClientFormRef = useRef()
  const addLeadFormRef = useRef()
  const clientProfileRef = useRef()
  const mobileNumber = useWatch("mobileNumber", initializeClientForm)
  const email = useWatch("email", initializeClientForm)
  const clientAdditionSource = useWatch("clientAdditionSource", initializeClientForm)
  const { affiliateId } = useWatch("searchAffiliate", initializeClientForm) ?? {}
  const user = useSelector(state => state.app.user?.info)
  const isAfilliateUser = useSelector(state => state.app.user?.info?.TeamType?.key) === "salesaff"
  const privileges = useSelector(state => state.app.privileges)
  const canAddClientUsingEmail = getPrivilege("can_add_client_using_email", privileges)
  const [
    checkPhone,
    {
      data: phoneData,
      isError: isPhoneError,
      error: phoneError,
      isFetching: phoneValidationFetching,
      isUninitialized: isCheckPhoneUninitialized,
    },
  ] = useLazyGetCheckPhoneNumberQuery()
  const [
    checkEmail,
    {
      data: emailData,
      isFetching: isEmailValidationFetching,
      isError: isEmailError,
      isUninitialized: isCheckEmailUninitialized,
    },
  ] = useLazyGetCheckEmailQuery()

  const [addLead, { isLoading: isAddLeadLoading, error, reset: resetAddLead }] = useAddLeadMutation()
  const [getClientDetail, { data: client, isFetching: isClientFetching, isUninitialized: isGetClientUninitialized }] =
    useLazyGetClientDetailQuery()
  const [
    changeAssignee,
    {
      data: changeAssigneeData,
      isLoading: isChangeAssigneeLoading,
      reset,
      isUninitialized: isChangeAssigneeUninitialized,
    },
  ] = useChangeAssigneeMutation()

  const { data: reasons, isFetching: fetchingReasons } = useGetReasonForAllocationQuery()

  const [stage, setStage] = useState(0)
  const [reason, setReason] = useState(null)
  const [errorState, setErrorState] = useState(null)
  const [clientFormValues, setClientFormValues] = useState(null)
  const [profileClient, setProfileClient] = useState(null)

  const [update] = useQueryUpdate()

  let rules = schemaRules(getClientLeadValidationSchema())
  const debounceEmailValidator = useCallback(
    _.debounce(value => {
      if (!isValidEmail(value)) {
        setErrorState(null)
        return
      }
      checkEmail({ email: value, matchedDetails: true, reAllocationCheck: true })
        .unwrap()
        .then(() => setErrorState(null))
        .catch(err => setErrorState(err?.data?.message ?? err?.message))
    }, 200),
    []
  )

  const disableButton = useMemo(() => {
    if (clientAdditionSource === "email") {
      return !email || !isValidEmail(email) || isEmailValidationFetching || isAddLeadLoading || !!emailData?.exists
    }

    const remianingConditions =
      isPhoneError ||
      phoneValidationFetching ||
      (phoneData?.exists && phoneData?.userId !== user.id) ||
      isChangeAssigneeLoading ||
      isClientFetching ||
      phoneData?.isUserCellNumber ||
      (phoneData?.isAllocationPossible && !phoneData?.isAllocationPossible) ||
      isAddLeadLoading

    return phoneData?.isAllocationPossible
      ? !!isAfilliateUser
        ? !affiliateId || !reason
        : !reason
      : remianingConditions
  }, [
    clientAdditionSource,
    email,
    emailData,
    errorState,
    isEmailValidationFetching,
    isAddLeadLoading,
    phoneData,
    phoneValidationFetching,
    isChangeAssigneeLoading,
    isClientFetching,
    reason,
    affiliateId,
  ])

  useEffect(() => {
    setStage(0)
  }, [])

  const onModalVisible = () => {
    setErrorState(null)
  }

  useEffect(() => {
    if (stage === 0) {
      setErrorState(null)
      if (clientAdditionSource === "phone") {
        const cell = initializeClientForm.getFieldValue("mobileNumber")
        if (cell && !isCheckPhoneUninitialized) {
          checkPhone({ cell })
            .unwrap()
            .catch(error => setErrorState(error?.message))
        }
      }
    }
    if (stage === 1) {
      const defaultValues = {
        ...(clientFormValues && clientFormValues),
        allocatedTo: {
          value: user.id,
          label: user.name,
          id: user.id.toString(),
        },
      }
      addClientFormRef.current?.setFieldsValue({
        ...defaultValues,
        ...(clientAdditionSource === "phone"
          ? { mobileNumber: [initializeClientForm.getFieldValue("mobileNumber")] }
          : { email: initializeClientForm.getFieldValue("email"), mobileNumber: [undefined] }),
      })
    }
  }, [stage, clientAdditionSource])

  const onReallocate = async () => {
    reset()
    try {
      await changeAssignee({
        clientIds: [phoneData?.data?.Client?.id],
        type: "add-client",
        allocatedTo: { id: user.id, name: user.name },
        reasonId: reason,
        requestReAllocation: true,
        referralId: affiliateId,
        referralType: !!isAfilliateUser ? "affiliate" : "direct",
        userPhone: mobileNumber,
      }).unwrap()
      handleStageChange("backward")
      notification.success({ message: t("Client has been re-allocated successfully") })
    } catch (e) {
      notify.error(e.message)
    }
  }

  const handleStageChange = (mode = "forward") => {
    if (mode === "forward") {
      if (stage === 0) {
        initializeClientForm.submit()
      }
      if (stage === 1) {
        addClientFormRef.current?.submit()
      }
      if (stage === 2) {
        addLeadFormRef.current?.submit()
      }
    }
    if (mode === "backward") {
      if (stage > 0) {
        setStage(prevStage => (client ? prevStage - 2 : prevStage - 1))
      } else resetForm()
    }
  }

  const resetForm = (clearProfile = true) => {
    ref?.current?.setVisible(false)
    setStage(0)
    setReason(null)
    setClientFormValues(null)
    clearProfile && setProfileClient(null)
    initializeClientForm.resetFields()
    addClientFormRef.current?.resetFields()
    addLeadFormRef.current?.resetFields()
    !isGetClientUninitialized && update("getClientDetail", draft => null)
    !isCheckPhoneUninitialized && update("getCheckPhoneNumber", draft => null)
    !isCheckEmailUninitialized && update("getCheckEmail", draft => null)
    resetAddLead()
  }

  const getLeadEventPayload = (leadValues, response) => ({
    ...ACTION_TYPES.add_new_client_submit,
    client_type: !!phoneData?.exists ? "existing" : "new",
    realocated: !!changeAssigneeData ? "yes" : "no",
    property_type: leadValues?.propertyType,
    property_sub_type: leadValues?.subType?.name,
    response,
    project_name: leadValues?.project?.name,
  })

  const handleAddLead = async (leadValues, { isNewClient } = {}) => {
    const clientAgainstLead = client ?? clientFormValues
    try {
      const response = await addLead({
        client: clientAgainstLead,
        addedWithEmail: clientAdditionSource === "email",
        ...leadValues,
      }).unwrap()
      const createdClientId = response?.inquiry?.clientId || clientAgainstLead?.id
      if (isNewClient && createdClientId) {
        try {
          const detailedClient = await getClientDetail(createdClientId).unwrap()
          setProfileClient(detailedClient)
        } catch (_error) {}
      }
      resetForm(false)
      if (isNewClient) {
        clientProfileRef?.current?.setVisible(true)
      }
      fireEvent(getLeadEventPayload(leadValues, "200"))
    } catch (e) {
      fireEvent(getLeadEventPayload(leadValues, e.status))
    }
  }

  const renderAllocationButton = () => {
    const isAllocationPossible = phoneData?.data && phoneData?.isAllocationPossible
    if (!isAllocationPossible) return null
    return (
      <SelectInput
        style={{ minWidth: 200 }}
        placeholder={t("Select Reason")}
        options={reasons}
        loading={fetchingReasons}
        getPopupContainer={false}
        allowClear
        onChange={value => setReason(value)}
      />
    )
  }

  const renderOkText = () => {
    if (stage === 0 && clientAdditionSource === "phone" && phoneData?.isAllocationPossible) {
      return t("Reallocate")
    }
    if (stage === 2) return t("Add Lead")
    return t("Next")
  }

  const renderAffiliateSection = () => {
    return (
      <>
        {!!isAfilliateUser ? (
          !!phoneData?.data &&
          phoneData?.isAllocationPossible &&
          clientAdditionSource === "phone" && (
            <>
              <Legend title={t("Affiliate Details")} />
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                <AffiliateSection rules={rules} form={initializeClientForm} subModuleName="editClient" />
              </Group>
            </>
          )
        ) : (
          <></>
        )}
      </>
    )
  }

  const renderExistingClientError = ({ reallocationData, isOwnedByUser, isAllocationPossible, source }) => {
    if (isOwnedByUser) {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["success-color"], size: "1em" }}
          textType="success"
          title={t("Client is already allocated to you.")}
        />
      )
    }
    if (isAllocationPossible) {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["success-color"], size: "1em" }}
          textType="success"
          title={t("The client exists and can be re-allocated to you. Please click on the Re-Allocate Client button.")}
        />
      )
    }
    if (source === "personal") {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={t("The client source is personal. Kindly request BSE team for manual reallocation of client.")}
        />
      )
    }
    return (
      <IconText
        icon="MdInfoOutline"
        iconProps={{ color: theme["error-color"], size: "1em" }}
        textType="danger"
        title={`${t("The client is active with")} ${reallocationData?.userName} - ${reallocationData?.userContactNumber} ${t("of")} ${reallocationData?.teamName} ${t("from")} ${reallocationData?.teamAddress}.\n ${t("Please contact him/her to share the client with you")}.`}
      />
    )
  }

  const renderAllocationMsg = () => {
    if (errorState) {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={errorState}
        />
      )
    }
    if (clientAdditionSource === "phone" && phoneData?.isUserCellNumber) {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={t("This cell no belongs to a staff user")}
        />
      )
    }
    if (clientAdditionSource === "phone" && phoneData?.data) {
      return renderExistingClientError({
        reallocationData: phoneData?.clientReallocationStatus?.data,
        isOwnedByUser: phoneData?.clientReallocationStatus?.data?.userId === user?.id,
        isAllocationPossible: phoneData?.isAllocationPossible,
        source: phoneData?.source,
      })
    }
  }

  const titleWithStageNavigation = () => {
    return (
      <Flex align="center" gap={4}>
        {stage > 0 && (
          <Button
            type="text"
            icon={<Icon icon={"IoIosArrowBack"} size="22px" color="#9d9d9d" />}
            onClick={() => {
              handleStageChange("backward")
            }}
            style={{ marginInlineStart: -12 }}
          />
        )}
        <div>{stage === 2 ? t("Add Lead") : t("Add Client")}</div>
      </Flex>
    )
  }

  return (
    <>
      <Modal
        title={titleWithStageNavigation()}
        ref={ref}
        width={900}
        onCancel={() => {
          resetForm()
        }}
        onOk={() => {
          const isReallocationCase = clientAdditionSource === "phone" && phoneData?.isAllocationPossible
          stage === 0 && isReallocationCase ? onReallocate() : handleStageChange("forward")
        }}
        okText={renderOkText()}
        disableOkButton={stage === 0 ? disableButton : false}
        hideCancelButton={true}
        onCancelProps={{
          onClick: () => {
            handleStageChange("backward")
          },
          cancelText: stage > 0 ? "Previous" : "Cancel",
        }}
        onModalVisible={onModalVisible}
        destroyOnClose
        footerAlert={error}
        loading={isAddLeadLoading || isChangeAssigneeLoading || isClientFetching}
      >
        <Form
          onKeyDown={e => {
            if (disableButton && e.key === "Enter") {
              e.preventDefault()
            }
          }}
          form={initializeClientForm}
          layout="vertical"
          name="client-form-initialize"
          initialValues={{
            mobileNumber: "",
            email: "",
            clientAdditionSource: "phone",
            searchAffiliate: affiliateOptions()?.[0],
          }}
          onFinish={async () => {
            const clientId = phoneData?.data?.Client?.id
            const nextStage = phoneData?.data?.Client?.User.id === user.id ? 2 : 1
            nextStage === 2 && getClientDetail(clientId)
            setStage(nextStage)
          }}
          scrollToFirstError
          preserve={true}
          hidden={stage !== 0}
        >
          <Legend title={t("Client Details")} />
          <Form.Item name="clientAdditionSource" className="mbe-16">
            <RadioButtons
              options={[
                { label: t("Phone Number"), value: "phone" },
                ...(canAddClientUsingEmail ? [{ label: t("Email"), value: "email" }] : []),
              ]}
              onChange={e => {
                initializeClientForm.setFieldsValue({
                  clientAdditionSource: e?.target?.value,
                  mobileNumber: "",
                  email: "",
                })
                setErrorState(null)
                setReason(null)
                update("getCheckPhoneNumber", draft => null)
                update("getCheckEmail", draft => null)
              }}
            />
          </Form.Item>
          <Row style={{ gap: 8 }} align="top">
            {clientAdditionSource === "phone" ? (
              <Form.Item
                label={t("Mobile Number")}
                required
                hasFeedback
                validateTrigger={["onBlur"]}
                name="mobileNumber"
                rules={getPhoneRules()}
                extra={renderAllocationMsg()}
                style={{ flexBasis: "48ch" }}
              >
                <PhoneInput
                  onChange={async input => {
                    if (input) {
                      setErrorState(null)
                      await checkPhone({ cell: input.split(" ").join(""), reAllocationCheck: true })
                        .unwrap()
                        .catch(error => setErrorState(error?.message))
                    }
                  }}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={t("Email")}
                required
                hasFeedback={isValidEmail(email) && !errorState}
                validateStatus={errorState ? "error" : undefined}
                validateTrigger={["onChange", "onBlur"]}
                name="email"
                rules={getEmailRules()}
                extra={renderAllocationMsg()}
                style={{ flexBasis: "48ch" }}
              >
                <TextInput
                  placeholder={t("Enter email")}
                  onChange={e => {
                    const val = e?.target?.value
                    setErrorState(null)
                    debounceEmailValidator(val)
                  }}
                />
              </Form.Item>
            )}

            {clientAdditionSource === "phone" && (
              <Row style={{ gap: 8, marginTop: 30, height: 46 }} align="middle">
                {phoneValidationFetching ? (
                  <>
                    <Spinner type="plain" />
                    <Text type="success">Validating this number</Text>
                  </>
                ) : (
                  !isPhoneError && renderAllocationButton()
                )}
              </Row>
            )}
          </Row>
          {renderAffiliateSection()}
        </Form>

        {stage === 1 && (
          <AddClientForm
            ref={addClientFormRef}
            preserve={true}
            clientAdditionSource={clientAdditionSource}
            allowEmailAdd={canAddClientUsingEmail}
            onSubmit={values => {
              setClientFormValues(values)
              setStage(2)
            }}
          />
        )}

      {stage === 2 && (client || clientFormValues) && (
        <AddLeadForm
          ref={addLeadFormRef}
          clientFormValues={clientFormValues}
          client={client}
          isNewClient={!!clientFormValues}
          onSubmit={handleAddLead}
          preserve={true}
        />
      )}
    </Modal>
    <ClientProfileForm
      formRef={clientProfileRef}
      client={profileClient}
      onSuccess={() => {
        clientProfileRef.current?.setVisible(false)
        setProfileClient(null)
      }}
    />
  </>
  )
})
//
export default ClientLeadForm
